import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './ChatRoom.css';
import Message from './Message';
import MessageInput from './MessageInput';
import PaymentModal from './PaymentModal';
import WinNotification from './WinNotification';
import Confetti from './Confetti';
import Notification from './Notification';
import ActiveUsers from './ActiveUsers';
import { io } from 'socket.io-client';
import api from '../utils/api';

const ChatRoom = ({ user, onLogout }) => {
  const [messages, setMessages] = useState([]);
  const [depositModal, setDepositModal] = useState(null);
  const [winNotification, setWinNotification] = useState(null);
  const [systemMessage, setSystemMessage] = useState(null);
  const [credits, setCredits] = useState(user.credits || 0);
  const [notification, setNotification] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [sandboxMode, setSandboxMode] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [activityFeed, setActivityFeed] = useState([]);
  const typingTimeoutsRef = useRef(new Map()); // Track timeouts per user
  const messagesEndRef = useRef(null);
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    // Connect to Socket.IO
    const newSocket = io(API_URL);

    // Join user-specific room for balance updates
    newSocket.emit('join', { userId: user.id });
    newSocket.emit('join', `user_${user.id}`);

    // Load initial messages and balance
    loadMessages();
    loadBalance();
    
    // Check sandbox mode status
    api.get('/api/sandbox-status')
      .then(response => {
        setSandboxMode(response.data.sandboxMode);
      })
      .catch(error => {
        console.error('Error checking sandbox status:', error);
      });

    // Listen for new messages
    // Append new messages to the end so the newest message is at the bottom
    newSocket.on('message:new', (message) => {
      setMessages(prev => [...prev, message]);
      scrollToBottom();
    });

    // Listen for upvote updates
    newSocket.on('message:upvote', (data) => {
      setMessages(prev => prev.map(msg => 
        msg.id === data.message_id 
          ? { ...msg, upvote_count: data.upvote_count }
          : msg
      ));
    });

    // Listen for balance updates
    newSocket.on('balance:updated', (data) => {
      setCredits(data.credits);
    });

    // Listen for warnings from admin
    newSocket.on('user:warned', (data) => {
      setNotification({
        message: `⚠️ WARNING: ${data.message}\n\nWarning count: ${data.warning_count}`,
        type: 'warning',
        duration: 10000
      });
    });

    // Listen for typing indicators with improved debouncing
    newSocket.on('user:typing', (data) => {
      if (data && data.username && data.username !== user.username) {
        // Clear existing timeout for this user
        const existingTimeout = typingTimeoutsRef.current.get(data.username);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }
        
        // Add user to typing set
        setTypingUsers(prev => new Set([...prev, data.username]));
        
        // Set timeout to remove after 5 seconds of no typing (longer to prevent flickering)
        const timeoutId = setTimeout(() => {
          setTypingUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.username);
            return newSet;
          });
          typingTimeoutsRef.current.delete(data.username);
        }, 5000);
        
        typingTimeoutsRef.current.set(data.username, timeoutId);
      }
    });

    // Listen for activity feed events with timestamps and auto-decay
    const addActivityItem = (text) => {
      const activityItem = {
        id: Date.now() + Math.random(), // Unique ID
        text: text,
        timestamp: Date.now(),
        fading: false
      };
      setActivityFeed(prev => [...prev, activityItem]);
      
      // Start fade-out 55 seconds after creation (5 seconds before removal)
      setTimeout(() => {
        setActivityFeed(prev => prev.map(item => 
          item.id === activityItem.id ? { ...item, fading: true } : item
        ));
      }, 55000);
      
      // Auto-remove after 1 minute
      setTimeout(() => {
        setActivityFeed(prev => prev.filter(item => item.id !== activityItem.id));
      }, 60000);
    };

    newSocket.on('activity:message', (data) => {
      if (data && data.username) {
        addActivityItem(`${data.username} just sent a message`);
      }
    });

    newSocket.on('activity:upvote', (data) => {
      if (data && data.username) {
        addActivityItem(`${data.username} just upvoted a message`);
      }
    });

    newSocket.on('activity:pool', (data) => {
      if (data && data.amount) {
        addActivityItem(`💎 Pool reached ${data.amount.toFixed(2)} DOGE!`);
      }
    });

    // Listen for system messages (payout announcements, etc.)
    newSocket.on('system:message', (data) => {
      setSystemMessage(data);
      setTimeout(() => setSystemMessage(null), 10000); // Hide after 10 seconds
    });

    // Listen for payout events
    newSocket.on('payout:starting', (data) => {
      console.log('Payout starting:', data);
      // Trigger confetti when timer hits 0 (payout starts)
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
      
      setNotification({
        type: 'info',
        message: '🏆 Payout process is starting...',
        duration: 5000
      });
    });

    newSocket.on('payout:complete', (data) => {
      console.log('Payout complete:', data);
      if (data.success) {
        // Trigger confetti for everyone when payout completes
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
        
        setNotification({
          type: 'success',
          message: `🎉 Payout completed! ${data.winners?.length || 0} winners paid out.`,
          duration: 10000
        });
        // Refresh balance in case user won
        loadBalance();
      } else {
        setNotification({
          type: 'error',
          message: `❌ Payout failed: ${data.error || 'Unknown error'}`,
          duration: 10000
        });
      }
    });

    // Listen for user-specific win notification
    newSocket.on('user:won', (data) => {
      console.log('User won:', data);
      // Show win notification modal
      setWinNotification({
        rank: data.rank,
        prize: data.prize,
        username: data.username
      });
      // Refresh balance
      loadBalance();
    });

    return () => {
      newSocket.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL, user.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    try {
      const response = await api.get('/api/messages?limit=100&page=1');
      // Messages come in DESC order (newest first), reverse to show oldest first (newest at bottom)
      setMessages(response.data.messages.reverse());
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const loadBalance = async () => {
    try {
      const response = await api.get('/api/payment/balance');
      setCredits(response.data.credits || 0);
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMessageSubmit = async (content) => {
    try {
      // Send message using credits (1 credit = 1 DOGE)
      const response = await api.post('/api/messages', { 
        content
      });
      
      // Update balance from response
      if (response.data.credits_remaining !== undefined) {
        setCredits(response.data.credits_remaining);
      }
      
      // Reload messages after a short delay to ensure the new message is in the database
      // The socket event should handle it immediately, but this ensures it persists after refresh
      setTimeout(() => {
        loadMessages();
      }, 1000);
      
      // Reload balance to be sure
      loadBalance();
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = error.response?.data?.error || 'Failed to send message';
      const details = error.response?.data?.message || '';
      
      if (error.response?.status === 403 && error.response?.data?.reason === 'no_credits_or_deposit') {
        setNotification({
          message: `${errorMsg}\n\nPlease make a deposit or receive credits to start chatting!`,
          type: 'warning',
          duration: 8000
        });
        // Optionally open deposit modal after a short delay
        setTimeout(() => {
          handleDeposit();
        }, 1000);
      } else if (error.response?.status === 400 && errorMsg === 'Insufficient credits') {
        setNotification({
          message: `${errorMsg}: ${details}\n\nPlease deposit more DOGE to continue chatting.`,
          type: 'warning',
          duration: 8000
        });
        // Optionally open deposit modal after a short delay
        setTimeout(() => {
          handleDeposit();
        }, 1000);
      } else {
        setNotification({
          message: errorMsg + (details ? `\n\n${details}` : ''),
          type: 'error'
        });
      }
    }
  };

  const handleDeposit = async () => {
    try {
      // Get deposit invoice (20 DOGE minimum)
      const depositResponse = await api.get('/api/payment/deposit');
      const { address, qrCode, paymentUrl, amount, creditsToAdd } = depositResponse.data;

      // Show deposit modal
      setDepositModal({
        address,
        qrCode,
        paymentUrl,
        amount,
        creditsToAdd,
        isDeposit: true
      });
    } catch (error) {
      console.error('Error initiating deposit:', error);
      setNotification({
        message: error.response?.data?.error || 'Failed to initiate deposit',
        type: 'error'
      });
    }
  };

  const handleUpvote = async (messageId) => {
    try {
      await api.post(`/api/messages/${messageId}/upvote`);
      // Socket.IO will handle the update
      setNotification({
        message: 'Upvote successful! ⬆️',
        type: 'success'
      });
    } catch (error) {
      console.error('Error upvoting:', error);
      const errorMsg = error.response?.data?.error || 'Failed to upvote';
      if (error.response?.data?.reason === 'no_credits_or_deposit') {
        setNotification({
          message: `${errorMsg}\n\nPlease make a deposit or receive credits to unlock upvoting!`,
          type: 'warning',
          duration: 8000 // Longer duration for important messages
        });
        // Optionally open deposit modal after a short delay
        setTimeout(() => {
          handleDeposit();
        }, 1000);
      } else {
        setNotification({
          message: errorMsg,
          type: 'error'
        });
      }
    }
  };

  const closeDepositModal = () => {
    setDepositModal(null);
    // Reload balance after closing modal (in case deposit was completed)
    loadBalance();
  };

  return (
    <div className="chatroom-container">
      <div className="chatroom-window win98-bevel">
        <div className="navigation-bar win98-bevel-inset">
          <div className="nav-links">
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
              💬 Chat
            </Link>
            <Link to="/pool" className={`nav-link ${location.pathname === '/pool' ? 'active' : ''}`}>
              💎 Pool
            </Link>
            <Link to="/leaderboard" className={`nav-link ${location.pathname === '/leaderboard' ? 'active' : ''}`}>
              🏆 Leaderboard
            </Link>
            <Link to="/settings" className={`nav-link ${location.pathname === '/settings' ? 'active' : ''}`}>
              ⚙️ Settings
            </Link>
            <Link to="/faq" className={`nav-link ${location.pathname === '/faq' ? 'active' : ''}`}>
              ❓ FAQ
            </Link>
            <Link to="/tos" className={`nav-link ${location.pathname === '/tos' ? 'active' : ''}`}>
              📜 Rules/ToS
            </Link>
            <a 
              href="https://discord.gg/B26JbHZsqq" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="nav-link discord-link"
              title="Join our Discord"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" viewBox="0 0 71 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0824 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0824 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 49.9718 48.5383C51.038 50.6034 52.2554 52.5699 53.5951 54.435C53.651 54.5139 53.7489 54.5477 53.8413 54.5195C59.646 52.7249 65.5286 50.0174 71.6015 45.5576C71.6551 45.5182 71.6887 45.459 71.6943 45.3942C73.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.2991 34.8216 17.2991 31.6709C17.2991 28.5203 20.1717 26.0166 23.7259 26.0166C27.308 26.0166 30.1626 28.4935 30.1526 31.6709C30.1426 34.8483 27.308 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.8911 34.8216 40.8911 31.6709C40.8911 28.5203 43.7636 26.0166 47.3178 26.0166C50.9 26.0166 53.7545 28.4935 53.7446 31.6709C53.7346 34.8483 50.9 37.3253 47.3178 37.3253Z" fill="#5865F2"/>
              </svg>
              Discord
            </a>
          </div>
        </div>
        <div className="chatroom-header">
          <div className="header-left">
            <h1 className="chatroom-title">
              <img src="/doge.png" alt="Doge" className="doge-logo" />
              Doge Of The Day
              <img src="/doge.png" alt="Doge" className="doge-logo" />
            </h1>
            <p className="chatroom-subtitle">Much Chat! Very Wow!</p>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span className="username">👤 {user.username}</span>
              <span className="credits">💰 {credits.toFixed(2)} Credits</span>
              <button onClick={handleDeposit} className="win98-button deposit-button">
                💳 Deposit
              </button>
            </div>
            <button onClick={onLogout} className="win98-button logout-button">
              Logout
            </button>
          </div>
        </div>

        {sandboxMode && (
          <div className="sandbox-banner win98-bevel-inset" style={{
            background: '#fff3cd',
            border: '2px inset #808080',
            padding: '10px 20px',
            margin: '10px 20px',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            🧪 SANDBOX MODE: Testing period - New users get 20 free credits. No payouts will be made during this time. All data will be reset in a few days once testing concludes.
          </div>
        )}

        {systemMessage && (
          <div className="system-message">
            {systemMessage.message}
          </div>
        )}

        <div className="messages-container win98-bevel-inset">
          <div className="messages-list">
            {messages.map(message => (
              <Message
                key={message.id}
                message={message}
                currentUserId={user.id}
                onUpvote={handleUpvote}
              />
            ))}
            {typingUsers.size > 0 && (
              <div className="typing-indicator win98-bevel-inset">
                {Array.from(typingUsers).join(', ')} {typingUsers.size === 1 ? 'is' : 'are'} typing...
                <span className="typing-dots">
                  <span>.</span><span>.</span><span>.</span>
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {activityFeed.length > 0 && (
          <div className="activity-feed">
            {activityFeed.slice().reverse().map((activity) => (
              <div 
                key={activity.id} 
                className={`activity-item ${activity.fading ? 'fading-out' : ''}`}
              >
                {activity.text}
              </div>
            ))}
          </div>
        )}

        <MessageInput 
          onSubmit={handleMessageSubmit} 
          credits={credits} 
          username={user.username}
        />

        <ActiveUsers />

        <div className="chatroom-footer">
          <p>💰 Each message costs 1 Credit • Each Credit ≈ 1 DOGE in the prize pool • 🏆 Daily contests reward top 3 messages! See FAQ for details.</p>
        </div>
      </div>

      {depositModal && (
        <PaymentModal
          address={depositModal.address}
          qrCode={depositModal.qrCode}
          paymentUrl={depositModal.paymentUrl}
          amount={depositModal.amount}
          creditsToAdd={depositModal.creditsToAdd}
          isDeposit={true}
          onClose={closeDepositModal}
        />
      )}

      {winNotification && (
        <WinNotification
          winData={winNotification}
          onClose={() => setWinNotification(null)}
        />
      )}

      {showConfetti && <Confetti trigger={showConfetti} />}

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          duration={notification.duration}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default ChatRoom;
